import { useEffect, useState, useMemo, useRef } from "react";
import { APIProvider, Map, Marker, InfoWindow } from "@vis.gl/react-google-maps";
import api from "../../lib/api";
import { Loader2, Eye, RefreshCw, MapPin, Car, Users as UsersIcon } from "lucide-react";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const REFRESH_MS = 8000; // poll every 8s; drivers push to backend at their own cadence

// SVG markers rendered as data URLs (top-down car silhouette).
// Green when the driver is on a course; Red when idle.
function carIcon(color, strokeDark = "#0A2540") {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <circle cx="20" cy="20" r="19" fill="${color}" fill-opacity="0.18"/>
    <circle cx="20" cy="20" r="12" fill="${color}" fill-opacity="0.32"/>
    <g transform="translate(20 20)">
      <path d="M -6 -9 L 6 -9 L 8 -3 L 8 8 L 6 10 L -6 10 L -8 8 L -8 -3 Z"
            fill="${color}" stroke="${strokeDark}" stroke-width="1.4" stroke-linejoin="round"/>
      <rect x="-5" y="-7.5" width="10" height="5.5" rx="1.2" fill="${strokeDark}" opacity="0.85"/>
      <rect x="-5" y="0.5"  width="10" height="4"   rx="1"   fill="${strokeDark}" opacity="0.55"/>
      <circle cx="-5.5" cy="-4.5" r="0.9" fill="#FFFFFF"/>
      <circle cx="5.5"  cy="-4.5" r="0.9" fill="#FFFFFF"/>
    </g>
  </svg>`;
  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: { width: 40, height: 40 },
    anchor: { x: 20, y: 20 },
  };
}

export default function AdminGodsEye() {
  const [viewMode, setViewMode] = useState("drivers"); // "drivers" | "vehicles"
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // driver_id
  const [lastUpdate, setLastUpdate] = useState(null);
  const [radiusCfg, setRadiusCfg] = useState(4);
  const [radiusDraft, setRadiusDraft] = useState(4);
  const [savingRadius, setSavingRadius] = useState(false);
  const timerRef = useRef(null);

  const fetch = async () => {
    try {
      const endpoint = viewMode === "vehicles" ? "/admin/vehicles/map" : "/admin/gods-eye";
      const { data } = await api.get(endpoint);
      // Normalise vehicles payload so the rest of the component works
      // unchanged (status, driver_name, lat, lng, driver_id).
      if (viewMode === "vehicles") {
        const items = (data.vehicles || []).map((v) => ({
          driver_id: v.id,
          driver_name: `${v.brand} ${v.model} · ${v.plate_number || ""}`.trim(),
          lat: v.lat,
          lng: v.lng,
          status: v.status === "on_trip" ? "on_trip" : "idle",
          vehicle_status: v.status,
          phone: v.driver_name,
          updated_at: v.location_updated_at,
        }));
        setDrivers(items);
      } else {
        setDrivers(Array.isArray(data) ? data : []);
      }
      setLastUpdate(new Date());
    } catch {
      /* silent */
    } finally { setLoading(false); }
  };

  useEffect(() => {
    setLoading(true);
    fetch();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(fetch, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Load the current rental discovery radius so admins can tune it from
  // the same panel where they monitor the fleet.
  useEffect(() => {
    api.get("/admin/rental-config").then(({ data }) => {
      setRadiusCfg(Number(data.radius_km) || 4);
      setRadiusDraft(Number(data.radius_km) || 4);
    }).catch(() => {});
  }, []);

  const saveRadius = async () => {
    const rk = Number(radiusDraft);
    if (!rk || rk <= 0) { return; }
    setSavingRadius(true);
    try {
      await api.put("/admin/rental-config", { radius_km: rk });
      setRadiusCfg(rk);
    } catch { /* toast handled by caller */ }
    finally { setSavingRadius(false); }
  };

  const onTrip = drivers.filter((d) => d.status === "on_trip");
  const idle   = drivers.filter((d) => d.status === "idle");

  // Center: average of all driver positions, fallback to Yaoundé
  const center = useMemo(() => {
    const withPos = drivers.filter((d) => d.lat && d.lng);
    if (withPos.length === 0) return { lat: 3.848, lng: 11.5021 };
    const lat = withPos.reduce((s, d) => s + d.lat, 0) / withPos.length;
    const lng = withPos.reduce((s, d) => s + d.lng, 0) / withPos.length;
    return { lat, lng };
  }, [drivers]);

  const greenIcon = useMemo(() => carIcon("#10B981"), []);
  const redIcon   = useMemo(() => carIcon("#EF4444"), []);

  const activeMarker = drivers.find((d) => d.driver_id === selected);

  if (!MAPS_KEY) {
    return (
      <div className="p-6 bg-[#FEE2E2] border border-[#EF4444] rounded-xl text-[#B91C1C] text-sm">
        La clé Google Maps n&apos;est pas configurée (`REACT_APP_GOOGLE_MAPS_API_KEY`).
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-gods-eye-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-[#FACC15]" />
          <h2 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
            God&apos;s Eye · Positions temps réel
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-[#CBD5E1] overflow-hidden">
            <button
              onClick={() => setViewMode("drivers")}
              className={`px-3 py-1.5 text-xs font-semibold ${viewMode === "drivers" ? "bg-[#0A2540] text-white" : "bg-white text-[#0A2540]"}`}
              data-testid="gods-eye-mode-drivers"
            >Chauffeurs</button>
            <button
              onClick={() => setViewMode("vehicles")}
              className={`px-3 py-1.5 text-xs font-semibold ${viewMode === "vehicles" ? "bg-[#0A2540] text-white" : "bg-white text-[#0A2540]"}`}
              data-testid="gods-eye-mode-vehicles"
            >Véhicules</button>
          </div>
          <button
            onClick={fetch}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A2540] text-white text-xs font-semibold hover:bg-[#081b2e]"
            data-testid="gods-eye-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Actualiser
          </button>
        </div>
      </div>

      {/* Rental discovery radius config */}
      <div className="flex items-center gap-3 flex-wrap bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl p-3" data-testid="rental-radius-config">
        <MapPin className="w-4 h-4 text-[#0A2540]" />
        <span className="text-xs font-semibold text-[#0A2540]">Rayon de découverte location :</span>
        <input
          type="number"
          min="0.5"
          max="100"
          step="0.5"
          value={radiusDraft}
          onChange={(e) => setRadiusDraft(e.target.value)}
          className="h-8 w-20 text-xs rounded border border-[#CBD5E1] px-2"
          data-testid="rental-radius-input"
        />
        <span className="text-xs text-[#64748B]">km</span>
        <button
          onClick={saveRadius}
          disabled={savingRadius || Number(radiusDraft) === Number(radiusCfg)}
          className="px-3 py-1 text-xs font-semibold rounded bg-[#FACC15] text-[#0A2540] disabled:opacity-40"
          data-testid="rental-radius-save"
        >
          {savingRadius ? "..." : "Enregistrer"}
        </button>
        <span className="text-[11px] text-[#64748B] ml-auto">
          Actuel : <strong>{radiusCfg} km</strong>
        </span>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 bg-white border border-[#CBD5E1] rounded-xl" data-testid="gods-eye-kpi-total">
          <p className="text-[10px] uppercase tracking-wide text-[#64748B] font-semibold">{viewMode === "vehicles" ? "Véhicules GPS actifs" : "Chauffeurs actifs"}</p>
          <p className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>{drivers.length}</p>
        </div>
        <div className="p-3 bg-[#DCFCE7] border border-[#10B981]/40 rounded-xl" data-testid="gods-eye-kpi-ontrip">
          <p className="text-[10px] uppercase tracking-wide text-[#065F46] font-semibold">En course</p>
          <p className="text-2xl font-bold text-[#059669]" style={{ fontFamily: "Outfit" }}>{onTrip.length}</p>
        </div>
        <div className="p-3 bg-[#FEE2E2] border border-[#EF4444]/40 rounded-xl" data-testid="gods-eye-kpi-idle">
          <p className="text-[10px] uppercase tracking-wide text-[#991B1B] font-semibold">{viewMode === "vehicles" ? "Visibles" : "Disponibles"}</p>
          <p className="text-2xl font-bold text-[#DC2626]" style={{ fontFamily: "Outfit" }}>{idle.length}</p>
        </div>
      </div>

      {/* Map */}
      <div className="relative w-full h-[600px] rounded-xl overflow-hidden border border-[#CBD5E1] bg-[#F8FAFC]" data-testid="gods-eye-map">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#F8FAFC]">
            <Loader2 className="w-6 h-6 animate-spin text-[#0A2540]" />
          </div>
        ) : (
          <APIProvider apiKey={MAPS_KEY}>
            <Map
              defaultCenter={center}
              defaultZoom={12}
              gestureHandling="greedy"
              disableDefaultUI={false}
              mapId="gods-eye"
            >
              {drivers.filter((d) => d.lat && d.lng).map((d) => (
                <Marker
                  key={d.driver_id}
                  position={{ lat: d.lat, lng: d.lng }}
                  icon={d.status === "on_trip" ? greenIcon : redIcon}
                  title={d.driver_name}
                  label={{
                    text: d.driver_name || "Chauffeur",
                    fontSize: "11px",
                    fontWeight: "700",
                    color: d.status === "on_trip" ? "#065F46" : "#991B1B",
                    className: "trigo-gods-eye-label",
                  }}
                  onClick={() => setSelected(d.driver_id)}
                />
              ))}
              {activeMarker && activeMarker.lat && activeMarker.lng && (
                <InfoWindow
                  position={{ lat: activeMarker.lat, lng: activeMarker.lng }}
                  onCloseClick={() => setSelected(null)}
                  pixelOffset={[0, -30]}
                >
                  <div style={{ fontFamily: "Outfit", minWidth: 200 }}>
                    <p style={{ fontWeight: 700, color: "#0A2540", margin: 0 }}>{activeMarker.driver_name}</p>
                    <p style={{
                      display: "inline-block",
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: 6,
                      marginTop: 4,
                      color: activeMarker.status === "on_trip" ? "#065F46" : "#991B1B",
                      background: activeMarker.status === "on_trip" ? "#DCFCE7" : "#FEE2E2",
                    }}>
                      {activeMarker.status === "on_trip" ? "🟢 En course" : "🔴 Disponible"}
                    </p>
                    {activeMarker.phone && (
                      <p style={{ fontSize: 12, color: "#64748B", margin: "4px 0 0" }}>📞 {activeMarker.phone}</p>
                    )}
                    {activeMarker.booking?.passenger_name && (
                      <p style={{ fontSize: 12, color: "#64748B", margin: "4px 0 0" }}>
                        Passager : <strong>{activeMarker.booking.passenger_name}</strong>
                      </p>
                    )}
                    <p style={{ fontSize: 10, color: "#94A3B8", marginTop: 6 }}>
                      MAJ : {activeMarker.updated_at ? new Date(activeMarker.updated_at).toLocaleTimeString("fr-FR") : "—"}
                    </p>
                  </div>
                </InfoWindow>
              )}
            </Map>
          </APIProvider>
        )}
      </div>

      {/* Legend + last update */}
      <div className="flex items-center justify-between text-xs text-[#64748B]">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#10B981]" /> En course</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#EF4444]" /> Disponible</span>
        </div>
        <span>Actualisé : {lastUpdate ? lastUpdate.toLocaleTimeString("fr-FR") : "—"} · auto-refresh {REFRESH_MS / 1000}s</span>
      </div>

      {/* Empty state */}
      {!loading && drivers.length === 0 && (
        <div className="p-6 border border-dashed border-[#CBD5E1] rounded-xl text-center text-sm text-[#64748B] bg-white" data-testid="gods-eye-empty">
          <MapPin className="w-6 h-6 mx-auto mb-2 text-[#CBD5E1]" />
          Aucun chauffeur avec une position GPS récente (≤ 30 min).
        </div>
      )}

      {/* Driver list (below map) */}
      {drivers.length > 0 && (
        <div className="bg-white border border-[#CBD5E1] rounded-xl overflow-hidden">
          <div className="p-3 bg-[#F1F5F9] flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
            <UsersIcon className="w-4 h-4" /> Détails ({drivers.length})
          </div>
          <table className="w-full text-sm" data-testid="gods-eye-list">
            <thead className="text-xs text-[#64748B] bg-[#FAFAFA]">
              <tr>
                <th className="p-2 text-left">Chauffeur</th>
                <th className="p-2 text-left">Statut</th>
                <th className="p-2 text-left">Téléphone</th>
                <th className="p-2 text-right">Dernière MAJ</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((d) => (
                <tr key={d.driver_id}
                    className="border-t border-[#F1F5F9] hover:bg-[#FEFCE8]/30 cursor-pointer"
                    onClick={() => setSelected(d.driver_id)}
                    data-testid={`gods-eye-row-${d.driver_id}`}>
                  <td className="p-2 font-semibold text-[#0A2540] flex items-center gap-2">
                    <Car className={`w-4 h-4 ${d.status === "on_trip" ? "text-[#10B981]" : "text-[#EF4444]"}`} />
                    {d.driver_name}
                  </td>
                  <td className="p-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${d.status === "on_trip" ? "bg-[#DCFCE7] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
                      {d.status === "on_trip" ? "En course" : "Disponible"}
                    </span>
                  </td>
                  <td className="p-2 text-[#64748B]">{d.phone || "—"}</td>
                  <td className="p-2 text-right text-xs text-[#64748B]">
                    {d.updated_at ? new Date(d.updated_at).toLocaleTimeString("fr-FR") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
